import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { validateSession, refreshToken } from '../../api/authService';
import { isTokenExpired, isTokenExpiringSoon } from '../../utils/jwtUtils';

interface UseSessionValidationOptions {
  /**
   * Intervalo en milisegundos para validar la sesión periódicamente
   * Por defecto: 5 minutos (300000 ms)
   */
  validationInterval?: number;
  /**
   * Minutos antes de la expiración para intentar refresh automático
   * Por defecto: 5 minutos
   */
  refreshThresholdMinutes?: number;
  /**
   * Si debe validar al cambiar de pestaña (visibilitychange)
   * Por defecto: true
   */
  validateOnVisibilityChange?: boolean;
  /**
   * Si debe validar inmediatamente al montar
   * Por defecto: true
   */
  validateOnMount?: boolean;
  /**
   * Callback cuando la sesión expira
   */
  onSessionExpired?: () => void;
}

/**
 * Hook para validar la sesión periódicamente y manejar expiración de tokens
 */
export const useSessionValidation = (options: UseSessionValidationOptions = {}) => {
  const {
    validationInterval = 5 * 60 * 1000, // 5 minutos
    refreshThresholdMinutes = 5,
    validateOnVisibilityChange = true,
    validateOnMount = true,
    onSessionExpired,
  } = options;

  const navigate = useNavigate();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);
  const validationInProgressRef = useRef(false);

  /**
   * Limpia la sesión y redirige al login
   */
  const handleSessionExpired = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.setItem('sessionExpired', 'true');

    if (onSessionExpired) {
      onSessionExpired();
    } else {
      navigate('/login', { replace: true });
    }
  }, [navigate, onSessionExpired]);

  /**
   * Intenta refrescar el token si está próximo a expirar
   */
  const tryRefreshToken = useCallback(async (): Promise<boolean> => {
    if (isRefreshingRef.current) {
      return false; // Ya hay un refresh en progreso
    }

    const accessToken = localStorage.getItem('accessToken');
    const refreshTokenValue = localStorage.getItem('refreshToken');

    // Si no hay refresh token, no podemos refrescar
    if (!refreshTokenValue) {
      return false;
    }

    // Si el access token no está próximo a expirar, no necesitamos refrescar
    if (!isTokenExpiringSoon(accessToken, refreshThresholdMinutes)) {
      return false;
    }

    // Si el refresh token está expirado, no podemos refrescar
    if (isTokenExpired(refreshTokenValue)) {
      return false;
    }

    try {
      isRefreshingRef.current = true;
      const newAccessToken = await refreshToken(refreshTokenValue);
      localStorage.setItem('accessToken', newAccessToken);
      return true;
    } catch (error) {
      console.error('Error al refrescar el token:', error);
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [refreshThresholdMinutes]);

  /**
   * Valida la sesión con el backend
   */
  const validate = useCallback(async (): Promise<boolean> => {
    // Evitar múltiples validaciones simultáneas
    if (validationInProgressRef.current) {
      return true;
    }

    validationInProgressRef.current = true;

    try {
      // Primero verificar si el token está expirado localmente
      const accessToken = localStorage.getItem('accessToken');
      if (isTokenExpired(accessToken)) {
        // Intentar refrescar si es posible
        const refreshed = await tryRefreshToken();
        if (!refreshed) {
          handleSessionExpired();
          return false;
        }
        // Después de refrescar, validar con el backend
      }

      // Validar con el backend
      const isValid = await validateSession();
      if (!isValid) {
        handleSessionExpired();
        return false;
      }

      // Si el token está próximo a expirar, intentar refrescar preventivamente
      if (isTokenExpiringSoon(accessToken, refreshThresholdMinutes)) {
        await tryRefreshToken();
      }

      return true;
    } catch (error) {
      console.error('Error validando sesión:', error);
      // En caso de error de red, no invalidar la sesión
      // Solo invalidar si es un error de autenticación
      return true;
    } finally {
      validationInProgressRef.current = false;
    }
  }, [tryRefreshToken, handleSessionExpired, refreshThresholdMinutes]);

  /**
   * Valida cuando la pestaña vuelve a estar visible
   */
  useEffect(() => {
    if (!validateOnVisibilityChange) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        validate();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [validate, validateOnVisibilityChange]);

  /**
   * Valida periódicamente
   */
  useEffect(() => {
    if (validationInterval <= 0) return;

    // Validar inmediatamente si está configurado
    if (validateOnMount) {
      validate();
    }

    // Configurar intervalo
    intervalRef.current = setInterval(() => {
      validate();
    }, validationInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [validate, validationInterval, validateOnMount]);

  return {
    validate,
    isRefreshing: isRefreshingRef.current,
  };
};

